import type { TFunction } from 'i18next';
import { type Persona, PersonaStatus } from './personasPayload.ts';

export type PersonaSection = {
  status: PersonaStatus;
  title: string;
  caption: string;
  personas: Persona[];
};

export const getPersonasDisclaimer = (t: TFunction) => ({
  label: t('Please note:'),
  text: t(
    'Personas are synthetic scenarios created to illustrate targeting logic. They do not represent real people, observed users, or predicted delivery.'
  ),
});

// The disclaimer leads the eligible examples, so it shows once per answer whether the personas come in one widget
// (ticket contract) or in a matching and a non-matching one (Targeting Explainer mock, PR #9240)
export const hasPersonasDisclaimer = (personas: Persona[]): boolean =>
  personas.some(persona => persona.status === PersonaStatus.MATCHING);

// please note that in current setup eligible personas will all always show up before the ineligible ones
export const getPersonaSections = (personas: Persona[], t: TFunction): PersonaSection[] => {
  const matching = personas.filter(persona => persona.status === PersonaStatus.MATCHING);
  const nonMatching = personas.filter(persona => persona.status === PersonaStatus.NON_MATCHING);

  const sections: PersonaSection[] = [
    {
      status: PersonaStatus.MATCHING,
      title: t('Eligible Examples ({total})', { total: matching.length }),
      caption: t('Synthetic personas who fit the targeting criteria.'),
      personas: matching,
    },
    {
      status: PersonaStatus.NON_MATCHING,
      title: t('Ineligible Examples ({total})', { total: nonMatching.length }),
      caption: t('Synthetic personas who fall outside the targeting criteria.'),
      personas: nonMatching,
    },
  ];

  return sections.filter(section => section.personas.length > 0);
};
